import React from 'react';
import classnames from 'classnames';
import { withTranslation, WithTranslation } from 'react-i18next';
import Draggable from 'react-draggable';
import { getHeightTillSubnavbar, ProfileHeroHeightNames } from '../../../';
import cssVars from '@wix/da-shared-react/src/styles/variables.scss';
import { PapiDeviation } from '@wix/da-types-papi';
import { getSourceImageRatio } from '@wix/da-shared-react/pkg/DeviationViews/Dumbo/Image/_helpers';
import MediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';

import * as s from './HeroPreview.scss';

const siteHeaderHeight = parseInt(cssVars['site-header-height']);

export interface Props {
  className?: string;
  coverDeviation: PapiDeviation;
  coverDeviationidOffsetY: number;
  reducedHero: boolean;
  onChangeOffsetY: (offsetY: number) => void;
}

export interface State {
  width: number;
  fullViewHeight: number;
  initViewHeight: number;
  showExpandedViewHints: boolean;
}

export class HeroPreview extends React.Component<
  Props & WithTranslation,
  State
> {
  fullViewNode: React.RefObject<HTMLDivElement> = React.createRef();

  state = {
    width: 0,
    fullViewHeight: 0,
    initViewHeight: 0,
    showExpandedViewHints: false,
  };

  componentDidMount() {
    const { reducedHero } = this.props;

    const fullVisibleHeightTillSubnavbar =
      getHeightTillSubnavbar(ProfileHeroHeightNames.Full, reducedHero) -
      siteHeaderHeight;
    const initVisibleHeightTillSubnavbar =
      getHeightTillSubnavbar(ProfileHeroHeightNames.Initial, reducedHero) -
      siteHeaderHeight;
    const fullToInitViewRatio =
      fullVisibleHeightTillSubnavbar / initVisibleHeightTillSubnavbar;
    const fullHeroAspectRatio =
      window.innerWidth / fullVisibleHeightTillSubnavbar;
    const fullViewHeight = this.fullViewNode.current!.offsetHeight;
    const width = Math.round(fullViewHeight * fullHeroAspectRatio);
    this.setState({
      width,
      fullViewHeight,
      initViewHeight: Math.round(fullViewHeight / fullToInitViewRatio),
    });
  }

  render() {
    const { t } = this.props;
    const { width, initViewHeight, showExpandedViewHints } = this.state;
    return (
      <div
        className={classnames(
          s['root'],
          showExpandedViewHints && s['with-expanded-view-hints']
        )}
        style={{ width }}
      >
        {width && this.renderCoverDeviationWithWrapper()}
        <div ref={this.fullViewNode} className={s['above-subnavbar-area']}>
          <div
            className={s['expanded-view-area']}
            onMouseEnter={this.showExpandedViewHints}
            onMouseLeave={this.hideExpandedViewHints}
          >
            <span className={s['expanded-view-hint']}>
              {t('common.profileHero.expandedView')}
            </span>
          </div>
          <div
            className={s['init-view-area']}
            style={{ height: initViewHeight }}
          >
            <span className={s['drag-to-move']}>
              {t('common.profileHero.dragToMove')}
            </span>
          </div>
        </div>
        <div
          className={classnames(
            s['expanded-view-area'],
            s['below-subnavbar-area']
          )}
          onMouseEnter={this.showExpandedViewHints}
          onMouseLeave={this.hideExpandedViewHints}
        >
          <span className={s['expanded-view-hint']}>
            {t('common.profileHero.expandedView')}
          </span>
        </div>
      </div>
    );
  }

  renderCoverDeviationWithWrapper = () => {
    const { coverDeviation, coverDeviationidOffsetY } = this.props;
    const { width } = this.state;
    const heightIfSquareHero = width; // hero is ~square on small resolutions (e.g. 1024px vw)
    if (!width) {
      return null;
    }
    const previewImage = MediaService.getDeviationFile(
      coverDeviation,
      'preview'
    );
    if (!previewImage) {
      return null;
    }
    const imageHeight = this.getImageHeightOnPreview();
    const spaceForAdjustment = imageHeight - heightIfSquareHero;
    const containerHeight = imageHeight + spaceForAdjustment;
    const offsetY = Math.round((imageHeight * coverDeviationidOffsetY) / 1000);
    return (
      <div
        className={s['cover-image-wrapper']}
        style={{ left: 0, top: -spaceForAdjustment, height: containerHeight }}
      >
        <Draggable
          defaultPosition={{ x: 0, y: -offsetY }}
          bounds="parent"
          onStop={this.onDragStop}
        >
          <div
            className={s['cover-image']}
            style={{
              width,
              height: imageHeight,
              backgroundImage: `url(${previewImage.src})`,
            }}
          />
        </Draggable>
      </div>
    );
  };

  showExpandedViewHints = () => {
    this.setState({ showExpandedViewHints: true });
  };

  hideExpandedViewHints = () => {
    this.setState({ showExpandedViewHints: false });
  };

  getImageHeightOnPreview = () => {
    const { coverDeviation } = this.props;
    const { width } = this.state;
    const ratio = getSourceImageRatio(coverDeviation);
    const imageHeight = Math.round(width / ratio);
    return imageHeight;
  };

  onDragStop = (e, position) => {
    this.props.onChangeOffsetY(
      Math.round((Math.abs(position.y) / this.getImageHeightOnPreview()) * 1000)
    );
  };
}

export default withTranslation()(HeroPreview);
